import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HarmonyLogo } from '../components/HarmonyLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '../components/ui/input-otp';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck, Loader2, AlertCircle, Smartphone, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Setup2FAPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { complete2FALogin } = useAuth();

  const tempToken = location.state?.tempToken;
  const user = location.state?.user;

  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [setupStep, setSetupStep] = useState('scan'); // 'scan' | 'verify'

  useEffect(() => {
    if (!tempToken) {
      navigate('/');
      return;
    }
    initSetup();
  }, [tempToken]);

  const initSetup = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/2fa/setup?temp_token=${encodeURIComponent(tempToken)}`
      );
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initialize 2FA setup. Please login again.');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    if (otpCode.length !== 6) {
      setError('Please enter the full 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/verify-setup`, {
        otp_code: otpCode,
        temp_token: tempToken,
        // First-time setup — trust the browser they just verified from.
        // They can revoke later from Profile → Trusted devices.
        remember_device: true,
      });

      const { token, user: userData } = response.data;
      complete2FALogin(token, userData);

      toast.success('Two-factor authentication enabled!', {
        description: 'Your account is now secured with 2FA'
      });

      if (userData.is_temporary_password) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split" data-testid="setup-2fa-page">
      <div className="flex flex-col justify-center items-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-lg space-y-6 animate-slide-up">
          {/* Header */}
          <div className="text-center space-y-3">
            <div className="flex justify-center">
              <HarmonyLogo size={80} />
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <ShieldCheck className="w-4 h-4" />
              Two-Factor Authentication Setup
            </div>
            {user && (
              <p className="text-sm text-muted-foreground">
                Setting up 2FA for <span className="font-medium">{user.full_name || user.username}</span>
              </p>
            )}
          </div>

          {setupLoading ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-16 flex flex-col items-center gap-4">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-muted-foreground">Generating your secure key...</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  {setupStep === 'scan' ? 'Step 1: Scan QR Code' : 'Step 2: Verify Code'}
                </CardTitle>
                <CardDescription>
                  {setupStep === 'scan' 
                    ? 'Open Google Authenticator and scan this QR code to add your account.'
                    : 'Enter the 6-digit code shown in your authenticator app.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {setupStep === 'scan' && (
                  <>
                    {/* QR Code */}
                    {qrCode && (
                      <div className="flex justify-center">
                        <div className="p-4 bg-white rounded-xl border-2 border-dashed border-muted" data-testid="qr-code-container">
                          <img 
                            src={`data:image/png;base64,${qrCode}`} 
                            alt="2FA QR Code" 
                            className="w-48 h-48"
                            data-testid="qr-code-image"
                          />
                        </div>
                      </div>
                    )}

                    {/* Manual entry secret */}
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground text-center">
                        Can't scan? Enter this key manually:
                      </p>
                      <div className="flex items-center gap-2 p-2.5 bg-muted/50 rounded-lg">
                        <code className="flex-1 text-xs font-mono text-center tracking-wider break-all" data-testid="secret-key">
                          {secret}
                        </code>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleCopySecret}
                          className="shrink-0 h-7 w-7 p-0"
                          data-testid="copy-secret-btn"
                        >
                          {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>

                    <Button 
                      className="w-full" 
                      onClick={() => { setSetupStep('verify'); setError(''); }}
                      data-testid="proceed-to-verify-btn"
                    >
                      I've scanned the QR code
                    </Button>
                  </>
                )}

                {setupStep === 'verify' && (
                  <form onSubmit={handleVerify} className="space-y-5">
                    <div className="flex justify-center" data-testid="setup-otp-input-group">
                      <InputOTP
                        maxLength={6}
                        value={otpCode}
                        onChange={setOtpCode}
                        autoFocus
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loading || otpCode.length !== 6}
                      data-testid="verify-setup-btn"
                    >
                      {loading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</>
                      ) : 'Verify & Enable 2FA'}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full text-muted-foreground"
                      onClick={() => { setSetupStep('scan'); setError(''); setOtpCode(''); }}
                    >
                      Back to QR code
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          )}

          <p className="text-center text-sm text-muted-foreground">
            Two-factor authentication is mandatory for all accounts.
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div 
        className="hidden lg:block login-image-panel"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1770488853538-08474909f4b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBicmlnaHR8ZW58MHx8fHwxNzcwNzI3NTM1fDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-primary/20 to-secondary/30" />
        <div className="relative z-10 flex flex-col justify-end h-full p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Secure Your Account</h2>
          <p className="text-lg opacity-90 max-w-md">
            Two-factor authentication adds an extra layer of security. 
            You'll need your authenticator app each time you sign in.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Setup2FAPage;
